import { useState, useEffect } from "react";
import api from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from "../components/ui/dialog";
import {
  HardDrive,
  Database,
  Download,
  Trash2,
  RefreshCw,
  AlertTriangle,
  CheckCircle2,
  Archive,
  Loader2,
  FileText
} from "lucide-react";
import { toast } from "sonner";

const formatDate = (dateString) => {
  return new Date(dateString).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  });
};

export default function AdminPage() {
  const [storage, setStorage] = useState(null);
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [deleteBackup, setDeleteBackup] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [storageRes, backupsRes] = await Promise.all([
        api.get("/admin/storage"),
        api.get("/admin/backups")
      ]);
      setStorage(storageRes.data);
      setBackups(backupsRes.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBackup = async () => {
    setCreating(true);
    try {
      const response = await api.post("/admin/backup");
      if (response.data.success) {
        toast.success(`Backup créé: ${response.data.filename}`);
        fetchData();
      } else {
        toast.error(`Erreur: ${response.data.error}`);
      }
    } catch (error) {
      toast.error("Erreur lors de la création du backup");
    } finally {
      setCreating(false);
    }
  };

  const handleDownloadBackup = async (filename) => {
    try {
      const response = await api.get(`/admin/backups/${filename}`, {
        responseType: "blob"
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleDeleteBackup = async () => {
    if (!deleteBackup) return;
    try {
      await api.delete(`/admin/backups/${deleteBackup}`);
      toast.success("Backup supprimé");
      setDeleteBackup(null);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Administration</h1>
          <p className="text-muted-foreground mt-1">
            Monitoring du stockage et gestion des sauvegardes
          </p>
        </div>
        <Button
          variant="outline"
          onClick={fetchData}
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualiser
        </Button>
      </div>

      {/* Storage Warning */}
      {storage?.storage_warning && (
        <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-200 text-amber-800">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <div>
            <p className="font-medium">Attention : Stockage élevé</p>
            <p className="text-sm">
              {storage.disk_usage_percent}% du disque utilisé. Pensez à supprimer des documents ou à augmenter l'espace.
            </p>
          </div>
        </div>
      )}

      {/* Storage Overview */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Espace disque
            </CardTitle>
            <HardDrive className="w-5 h-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {storage?.disk_free_gb} GB
            </div>
            <p className="text-xs text-muted-foreground">
              disponibles sur {storage?.disk_total_gb} GB
            </p>
            <Progress
              value={storage?.disk_usage_percent || 0}
              className="mt-3 h-2"
            />
            <p className="text-xs text-muted-foreground mt-1">
              {storage?.disk_usage_percent}% utilisé
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Documents
            </CardTitle>
            <FileText className="w-5 h-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {storage?.uploads_count || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              fichiers ({storage?.uploads_size_mb || 0} MB)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Sauvegardes
            </CardTitle>
            <Archive className="w-5 h-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {storage?.backups_count || 0}
            </div>
            <p className="text-xs text-muted-foreground">
              backups ({storage?.backups_size_mb || 0} MB)
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Limite fichier
            </CardTitle>
            <Database className="w-5 h-5 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {storage?.max_file_size_mb || 50} MB
            </div>
            <p className="text-xs text-muted-foreground">
              taille max par document
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Storage by Category */}
      {storage?.category_sizes_mb && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Stockage par catégorie</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3 lg:grid-cols-6">
              {Object.entries(storage.category_sizes_mb).map(([category, sizeMb]) => (
                <div key={category} className="text-center p-4 rounded-lg bg-muted/50">
                  <div className="text-lg font-bold">{sizeMb} MB</div>
                  <Badge variant="secondary" className="mt-1">
                    {category}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Backups */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Archive className="w-5 h-5" />
            Sauvegardes
          </CardTitle>
          <Button
            onClick={handleCreateBackup}
            disabled={creating}
            data-testid="create-backup-btn"
          >
            {creating ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Archive className="w-4 h-4 mr-2" />
            )}
            Créer une sauvegarde
          </Button>
        </CardHeader>
        <CardContent>
          {backups.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Archive className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>Aucune sauvegarde</p>
              <p className="text-sm mt-1">
                Créez une première sauvegarde pour protéger vos données
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {backups.map((backup) => (
                <div
                  key={backup.filename}
                  className="flex items-center justify-between p-4 rounded-lg border bg-muted/30"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-green-600" />
                    </div>
                    <div>
                      <p className="font-medium">{backup.filename}</p>
                      <p className="text-sm text-muted-foreground">
                        {backup.size_mb} MB - {formatDate(backup.created_at)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownloadBackup(backup.filename)}
                      data-testid={`download-${backup.filename}`}
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setDeleteBackup(backup.filename)}
                      className="text-destructive hover:text-destructive"
                      data-testid={`delete-${backup.filename}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <p className="text-xs text-muted-foreground text-center mt-4">
            Les 5 dernières sauvegardes sont conservées automatiquement
          </p>
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <Dialog open={!!deleteBackup} onOpenChange={() => setDeleteBackup(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer la sauvegarde ?</DialogTitle>
          </DialogHeader>
          <p className="text-muted-foreground">
            Cette action est irréversible. La sauvegarde sera définitivement supprimée.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteBackup(null)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDeleteBackup}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
